import React, {useCallback, useEffect, useState} from "react";
import {Box, Button, CircularProgress, Grid, IconButton, Menu, MenuItem, Stack, Tooltip, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Add01Icon, MoreVerticalIcon, Refresh01Icon, Route01Icon} from "@hugeicons/core-free-icons";
import {green, grey, red} from "@mui/material/colors";
import {toast} from "react-toastify";
import {AmiRepository} from "../../../repositories/AmiRepository";
import {ExtensionCard} from "./components/ExtensionCard";
import {AddExtensionModal} from "./components/AddExtensionModal";

export const DialplanPage = ({project}) => {
  const flowUid = project?.id;

  const [connected, setConnected] = useState(null);
  const [extensions, setExtensions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [addOpen, setAddOpen] = useState(false);

  const fetchHealth = useCallback(() => {
    if (!flowUid) return;
    setConnected(null);
    AmiRepository.getHealth(flowUid)
      .then((res) => setConnected(!!res?.data?.connected))
      .catch(() => setConnected(false));
  }, [flowUid]);

  const fetchExtensions = useCallback(() => {
    if (!flowUid) return;
    setLoading(true);
    AmiRepository.listExtensions(flowUid)
      .then((res) => setExtensions(res?.data?.extensions || []))
      .catch(() => toast.error("Failed to load extensions"))
      .finally(() => setLoading(false));
  }, [flowUid]);

  const refreshAll = useCallback(() => {
    fetchHealth();
    fetchExtensions();
  }, [fetchHealth, fetchExtensions]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  const handleReloadDialplan = () => {
    setReloading(true);
    AmiRepository.reloadDialplan(flowUid)
      .then((res) => {
        if (res?.data?.dialplan_reloaded) toast.success("Dialplan reloaded");
        else toast.error("Dialplan reload did not complete");
      })
      .catch((err) => toast.error(err?.response?.data?.error || "Failed to reload dialplan"))
      .finally(() => setReloading(false));
  };

  const handleRegenerateConf = () => {
    setMenuAnchor(null);
    AmiRepository.regenerateConf(flowUid)
      .then((res) => {
        if (res?.data?.conf_regenerated) toast.success("Configuration file regenerated");
        else toast.error(res?.data?.conf_error || "Failed to regenerate configuration file");
      })
      .catch((err) => toast.error(err?.response?.data?.error || "Failed to regenerate configuration file"));
  };

  const handleResync = () => {
    setMenuAnchor(null);
    AmiRepository.resyncExtensions(flowUid)
      .then((res) => {
        const {synced = 0, failed = 0} = res?.data || {};
        toast.success(`Resynced ${synced} extension(s)${failed ? `, ${failed} failed` : ""}`);
      })
      .catch((err) => toast.error(err?.response?.data?.error || "Failed to resync extensions"));
  };

  const handleExtensionSaved = (updated) => {
    setExtensions((prev) => {
      const exists = prev.some((e) => e.extension === updated.extension);
      return exists
        ? prev.map((e) => (e.extension === updated.extension ? {...e, ...updated} : e))
        : [...prev, updated];
    });
  };

  const handleExtensionDeleted = (deleted) => {
    setExtensions((prev) => prev.filter((e) => e.id !== deleted.id));
  };

  const dotColor = connected === null ? grey[400] : connected ? green[500] : red[500];

  return (
    <>
      <AddExtensionModal
        open={addOpen}
        flowUid={flowUid}
        handleClose={() => setAddOpen(false)}
        onCreated={(created) => {
          handleExtensionSaved(created);
          setAddOpen(false);
        }}
      />
      <Grid maxWidth={"lg"} sx={{mx: "auto", p: 3, my: 5, borderRadius: 4, bgcolor: "#fefefeee"}} container className={"border-shadow"}>
        <Grid item xs={12}>
          <Stack direction={"row"} gap={2} justifyContent={"space-between"} alignItems={"center"} flexWrap={"wrap"}>
            <Box>
              <Stack direction={"row"} gap={1} alignItems={"center"}>
                <HugeiconsIcon icon={Route01Icon} size={22}/>
                <Typography variant={"h4"} fontWeight={600}>SIP Dialplan</Typography>
                <Tooltip title={connected === null ? "Checking AMI connection..." : connected ? "AMI connected" : "AMI disconnected"}>
                  <Box sx={{width: 10, height: 10, borderRadius: "50%", bgcolor: dotColor, ml: 0.5}}/>
                </Tooltip>
              </Stack>
              <Typography variant={"body2"} color={"text.secondary"} mt={0.5}>
                Manage registered dialplan extensions for this flow
              </Typography>
            </Box>
            <Stack direction={"row"} gap={1} alignItems={"center"}>
              <Button
                sx={{boxShadow: 0}}
                variant={"ghost"}
                color={"secondary"}
                startIcon={loading ? <CircularProgress size={16}/> : <HugeiconsIcon icon={Refresh01Icon} size={18}/>}
                onClick={refreshAll}
                disabled={loading || !flowUid}
              >
                Refresh
              </Button>
              <Button
                sx={{boxShadow: 0}}
                variant={"modal"}
                color={"secondary"}
                startIcon={reloading ? <CircularProgress size={16} color={"inherit"}/> : <HugeiconsIcon icon={Refresh01Icon} size={18}/>}
                onClick={handleReloadDialplan}
                disabled={reloading || !flowUid}
              >
                Reload Dialplan
              </Button>
              <Button
                className={"text-primary-gradient"}
                sx={{boxShadow: 0}}
                startIcon={<HugeiconsIcon icon={Add01Icon} size={18}/>}
                onClick={() => setAddOpen(true)}
                disabled={!flowUid}
              >
                Add Extension
              </Button>
              <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)} disabled={!flowUid}>
                <HugeiconsIcon icon={MoreVerticalIcon} size={18}/>
              </IconButton>
              <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
                <MenuItem onClick={handleRegenerateConf}>Regenerate Config File</MenuItem>
                <MenuItem onClick={handleResync}>Resync Extensions to Asterisk</MenuItem>
              </Menu>
            </Stack>
          </Stack>
        </Grid>

        <Grid item xs={12} sx={{mt: 3}}>
          {loading && extensions.length === 0 ? (
            <Stack alignItems={"center"} py={4}><CircularProgress size={24}/></Stack>
          ) : extensions.length === 0 ? (
            <Typography align={"center"} color={"text.secondary"} sx={{py: 4}}>
              No extensions registered yet
            </Typography>
          ) : (
            <Stack gap={1.5}>
              {extensions.map((ext) => (
                <ExtensionCard
                  key={ext.id || ext.extension}
                  extension={ext}
                  flowUid={flowUid}
                  onSaved={handleExtensionSaved}
                  onDeleted={handleExtensionDeleted}
                />
              ))}
            </Stack>
          )}
        </Grid>
      </Grid>
    </>
  );
};
